import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import TableCard from "../../components/ui-components/TableCard";
import EmptyState from "../../components/ui-components/EmptyState";
import Skeleton from "../../components/ui-components/Skeleton";
import FlexTable from "../../components/ui-components/FlexTable";
import { useConversationsQuery, usePatchConversationMutation, useDeleteConversationMutation, analysisKeys } from "./hooks";

// icon() is a compile-time macro — each style variant must be its own literal call.
const pinnedIcon = icon({ name: "thumbtack", style: "solid" });
const unpinnedIcon = icon({ name: "thumbtack", style: "light" });

export default function AnalysisListPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: items = [], isLoading, error } = useConversationsQuery();
  const patchMutation = usePatchConversationMutation();
  const deleteMutation = useDeleteConversationMutation();

  const columns = useMemo(
    () => [
      {
        Header: "Title",
        accessor: "title",
        Cell: ({ row }) => (
          <div className="min-w-0 truncate font-medium text-text-2">
            {row.original.title || "Untitled analysis"}
          </div>
        ),
      },
      {
        Header: "Updated",
        accessor: "updatedAt",
        width: 140,
        align: "right",
        Cell: ({ value }) => (
          <span className="text-text-3">{value ? new Date(value).toLocaleString() : "—"}</span>
        ),
      },
      {
        Header: "",
        accessor: "id",
        width: 90,
        align: "right",
        Cell: ({ row }) => {
          const conv = row.original;
          const pinned = !!conv.pinnedAt;
          return (
            <div className="flex items-center gap-1">
              <button
                type="button"
                title={pinned ? "Unpin" : "Pin"}
                aria-label={pinned ? "Unpin analysis" : "Pin analysis"}
                className={`rounded-ctrl px-1 transition-colors ${
                  pinned ? "text-primary" : "text-text-2 hover:text-primary"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  patchMutation.mutate({ id: conv.id, pinned: !pinned });
                }}
              >
                <FontAwesomeIcon icon={pinned ? pinnedIcon : unpinnedIcon} className="w-4 h-4" />
              </button>
              <button
                type="button"
                title="Delete"
                aria-label="Delete analysis"
                className="rounded-ctrl px-1 text-text-2 transition-colors hover:bg-danger-soft hover:text-danger"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteMutation.mutate(conv.id);
                }}
              >
                <FontAwesomeIcon icon={icon({ name: "trash-can", style: "light" })} className="w-4 h-4" />
              </button>
            </div>
          );
        },
      },
    ],
    [patchMutation, deleteMutation]
  );

  return (
    <>
      <AdministrationHeader title="Ask InstantView" className="text-text-3" showPeriodSelector={false} />
      <div className="w-full p-8">
        <TableCard
          title="Ask InstantView"
          subtitle="Your analysis history — questions asked and answered"
          primaryAction={{ label: "New analysis", icon: icon({ name: "plus" }), onClick: () => navigate("/") }}
          error={error}
          errorMessage="Error loading analyses"
          onRetry={() => qc.invalidateQueries({ queryKey: analysisKeys.all })}
        >
          {isLoading ? (
            <div className="space-y-3 py-2">
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} width="100%" height={52} />
              ))}
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={icon({ name: "comments", style: "light" })}
              title="No analyses yet"
              message="Ask a question about your cloud & AI spend, usage, forecasts or budgets to get started."
              action={{ label: "New analysis", onClick: () => navigate("/"), icon: icon({ name: "plus" }) }}
            />
          ) : (
            <FlexTable
              columns={columns}
              data={items}
              onRowClick={(id) => navigate(`/analysis/${id}`)}
              rowAriaLabel={(r) => `Open ${r.title || "Untitled analysis"}`}
            />
          )}
        </TableCard>
      </div>
    </>
  );
}
